/* Prices are display configuration. Shopify Discounts must enforce matching totals. */
(() => {
  if (customElements.get('uplief-native-bundles')) return;
  const cents = value => /^\d+(\.\d{1,2})?$/.test(String(value)) ? Math.round(Number(value) * 100) : NaN;
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  customElements.define('uplief-native-bundles', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.querySelectorAll('.ub-card').forEach(card=>card.remove());
      this.form = this.closest('form');
      this.message = this.querySelector('.ub-message');
      this.preview = this.dataset.preview === 'true';
      this.ready = false;
      this.selectedVariants = [];
      this.selectedOptions = [];
      this.controller = new AbortController();
      // OCU's single-item form serializer appends an invalid id to multi-item forms.
      // Submit this selector's bundle once as Shopify's documented JSON items array,
      // then refresh/open OCU through its public drawer event. Checkout upsells remain OCU-owned.
      // OCU also submits from document touchend, before the browser generates click.
      // Stop that serializer for this form only; preserve the normal generated click
      // so taps and keyboard/mouse clicks all use the JSON submission below.
      window.addEventListener('touchend', event => {
        const button=event.target.closest?.('button[name="add"]');
        if(!button || button.form!==this.form || this.preview) return;
        event.stopImmediatePropagation();
      }, {capture:true,passive:true,signal:this.controller.signal});
      window.addEventListener('click', event => {
        const button=event.target.closest?.('button[name="add"]');
        if(!button || button.form!==this.form || this.preview) return;
        event.preventDefault(); event.stopImmediatePropagation();
        this.addToCart();
      }, {capture:true,signal:this.controller.signal});
      window.addEventListener('submit', event => {
        if(event.target!==this.form || this.preview) return;
        event.preventDefault(); event.stopImmediatePropagation();
        this.addToCart();
      }, {capture:true,signal:this.controller.signal});
      this.form.addEventListener('submit', event => {
        if (!this.canPurchase()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.message.textContent = this.preview ? 'Design preview — purchasing is disabled.' : 'Please check your selection before adding to cart.';
        }
      }, { capture:true, signal:this.controller.signal });
      this.form.addEventListener('formdata', event => {
        if (this.canPurchase()) this.writeItems(event.formData);
      }, { signal:this.controller.signal });
      try {
        this.config = JSON.parse(this.querySelector('[data-ub-config]').textContent);
        const c = this.config;
        this.unitName = c.handle === 'uplief-adjustable-posture-support' ? 'item' : 'pair';
        this.unitPlural = `${this.unitName}s`;
        this.variants = c.variants;
        this.setupSizeChart();
        this.base = c.baseCents;
        if (c.currency !== 'SGD' || !Number.isSafeInteger(this.base) || this.base <= 0 || this.variants.some(v => v.price !== this.base)) {
          throw new Error('These offers need a pricing update. Please contact us for assistance.');
        }
        const enabledOffers=(c.offers || []).filter(t=>t.enabled).sort((a,b)=>a.slot-b.slot).map(t=>({
          ...t, total:cents(t.total), original:cents(t.original), errors:[]
        }));
        if(!enabledOffers.length) throw new Error('Enable at least one offer.');
        this.tiers=[];
        this.pendingOffers=[];
        let previous;
        enabledOffers.forEach(t=> {
          if(!Number.isSafeInteger(t.quantity) || t.quantity<1 || t.quantity>100) t.errors.push(`Enter a whole number of ${this.unitPlural} from 1 to 100.`);
          else if(previous && t.quantity<=previous.quantity) t.errors.push(`Use more than ${previous.quantity} ${this.unitPlural} (Offer ${previous.slot}).`);
          if(!Number.isSafeInteger(t.total) || t.total<=0) t.errors.push('Enter a sale total.');
          else if(t.total>this.base*t.quantity) t.errors.push('The sale total exceeds the Shopify product subtotal. Update the product price or this offer.');
          if(t.showQuantity && Number.isSafeInteger(t.total) && t.total%t.quantity!==0) t.errors.push(`Turn off Show quantity selector or use an exact per-${this.unitName} price.`);
          if(t.errors.length) this.pendingOffers.push(t);
          else { this.tiers.push(t);previous=t; }
        });
        if(this.pendingOffers.length && !this.preview) throw new Error('An offer is temporarily unavailable. Please contact us for assistance.');
        this.tiers.forEach((t,i)=>t.maxQuantity=this.tiers[i+1]?.quantity-1 || 100);
        this.highest=this.tiers.at(-1);
        if(!this.tiers.length) {
          this.pendingOffers.forEach(t=>this.makePendingCard(t));
          this.message.textContent='Complete the offer settings to preview your prices.';
          this.lockButtons(true);
          return;
        }
        this.initial = this.variants.find(v => v.id === c.initialVariant && v.available) || this.variants.find(v => v.available);
        if (!this.initial) throw new Error('This product is currently sold out.');
        this.money = value => new Intl.NumberFormat(document.documentElement.lang || 'en-SG', {style:'currency',currency:'SGD',currencyDisplay:'narrowSymbol',minimumFractionDigits:2,maximumFractionDigits:2}).format(value/100);
        this.setupAddon();
        this.renderDiscountGuide();
        this.cards = this.tiers.map((tier,index) => this.makeCard(tier,index));
        this.pendingOffers.forEach(t=>this.makePendingCard(t));
        const offerList=this.querySelector('.ub-offers');
        [...offerList.querySelectorAll('.ub-card')].sort((a,b)=>Number(a.dataset.offerSlot)-Number(b.dataset.offerSlot)).forEach(card=>offerList.append(card));
        const defaultIndex=this.tiers.findIndex(t=>t.selectedDefault);
        this.selectTier(defaultIndex<0 ? 0 : defaultIndex);
        this.ready = true;
        this.updateState();
        if (!this.preview) {
          const checkConflict=()=> {
            if (this.ready && document.querySelector('.kaching-bundles, kaching-bundles')) {
              this.ready=false;
              this.lockButtons(true);
              this.message.textContent='Bundle selection is temporarily unavailable. Please contact us for assistance.';
            }
          };
          this.observer=new MutationObserver(records=> {
            if(records.some(record=>[...record.addedNodes].some(node=>
              node.nodeType===1 && (node.matches('.kaching-bundles, kaching-bundles') || node.querySelector('.kaching-bundles, kaching-bundles'))
            ))) checkConflict();
          });
          this.observer.observe(this.closest('main') || document.body,{childList:true,subtree:true});
          checkConflict();
        }
      } catch (error) {
        this.message.textContent = error.message;
        this.lockButtons(true);
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.initialized = false; }
    async waitForCartInitialization() {
      if (!window.Zipify?.Cart?.assetUrl) return;
      // OCU starts an attributes-only /cart/update.js request on an empty cart.
      // Adding before that response finishes can replace the new cart session
      // with the initial empty one. Its loaded event precedes that response.
      const ready = () => {
        const ocu = window.Zipify?.Cart;
        if (!ocu?.instance?.isLoaded) return false;
        if (ocu.cart?.item_count > 0) return true;
        return performance.getEntriesByType('resource').some(entry => {
          try { return new URL(entry.name).pathname === `${window.Shopify?.routes?.root || '/'}cart/update.js`; }
          catch { return false; }
        });
      };
      const deadline = Date.now() + 12000;
      while (!ready()) {
        if (Date.now() >= deadline) throw new Error('Your cart is still loading. Please try again in a moment.');
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    async addToCart() {
      if(this.adding || !this.canPurchase()) return;
      this.adding=true;
      this.lockButtons(true);
      this.message.textContent='';
      const productForm=this.form.closest('product-form');
      const cart=productForm?.cart;
      const button=this.form.querySelector('button[name="add"]');
      button?.setAttribute('aria-busy','true');
      try {
        await this.waitForCartInitialization();
        const groups=new Map();
        this.selectedVariants.slice(0,this.quantity).forEach(id=>groups.set(id,(groups.get(id)||0)+1));
        const addonCheck=this.querySelector('[data-ub-addon-check]');
        const addonVariant=this.querySelector('[data-ub-addon-variant]');
        if(addonCheck?.checked && addonVariant?.value) {
          const id=String(addonVariant.value);
          groups.set(id,(groups.get(id)||0)+1);
        }
        const body={items:[...groups].map(([id,quantity])=>({id,quantity}))};
        if(!window.UpliefAmpAddon && document.querySelector('#amp-embedded-product-upsell input[type="checkbox"]:checked')) {
          throw new Error('The complementary offer is still loading. Please try again in a moment.');
        }
        const ampAddon=window.UpliefAmpAddon?.getSelectedItem(this.form);
        if(ampAddon) body.items.push(ampAddon);
        // OCU renders its own drawer. Avoid asking Shopify to render unused theme
        // cart sections on every add; retain them for the theme-cart fallback.
        const useOcu=Boolean(window.Zipify?.Cart?.instance);
        if(!useOcu && cart?.getSectionsToRender) {
          body.sections=cart.getSectionsToRender().map(section=>section.id);
          body.sections_url=window.location.pathname;
          cart.setActiveElement?.(button);
        }
        const response=await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`,{
          method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify(body)
        });
        // Shopify's .js endpoints can return JSON with a JavaScript media type.
        // Parse the body instead of rejecting a successful add by its header.
        let result;
        try {
          result=await response.json();
        } catch {
          throw new Error(response.status===429
            ? 'The store is checking your connection. Please wait a moment, then try again.'
            : 'Unable to confirm your cart. Please check your cart before adding again.');
        }
        if(!response.ok || result.status) throw new Error(result.description || `Unable to add these ${this.unitPlural}. Please try again.`);
        window.UpliefAmpAddon?.clearSelection(this.form);
        this.message.textContent='';
        if(useOcu) window.dispatchEvent(new Event('ocu:cart:open:updated'));
        else if(cart?.renderContents) cart.renderContents(result);
        else window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      } catch(error) {
        this.message.textContent=error.message || `Unable to add these ${this.unitPlural}. Please try again.`;
      } finally {
        this.adding=false;
        this.lockButtons(!this.canPurchase());
        button?.removeAttribute('aria-busy');
      }
    }
    renderDiscountGuide() {
      const container=this.querySelector('[data-ub-discount-guide]');
      if(!container) return;
      container.replaceChildren(el('p','',`Shopify selling price: ${this.money(this.base)} per ${this.unitName}. Discount per ${this.unitName} = Shopify selling price − sale total ÷ quantity.`));
      const list=el('ul');
      this.tiers.forEach(t=> {
        const reduction=this.base*t.quantity-t.total;
        let instruction=`Offer ${t.slot}: ${t.quantity} ${this.unitPlural} for ${this.money(t.total)}. `;
        if(reduction===0) instruction+='No discount needed at this quantity.';
        else if(reduction%t.quantity===0) instruction+=`${this.money(reduction/t.quantity)} off each ${this.unitName}; minimum ${t.quantity} ${this.unitPlural}. At this rate, ${t.quantity+1} ${this.unitPlural} cost ${this.money(t.total/t.quantity*(t.quantity+1))}, unless a better tier applies.`;
        else instruction+=`No exact per-${this.unitName} discount in whole cents. ${this.money(reduction)} off once per order gives this total only at ${t.quantity} ${this.unitPlural}. Do not use this as a repeating bundle rule; extra quantities and mixed variants need checkout testing.`;
        const better=this.tiers.filter(other=>other.quantity<t.quantity && other.total%other.quantity===0 && other.total/other.quantity*t.quantity<t.total);
        if(better.length) instruction+=` Conflict: Offer ${better[0].slot}'s per-${this.unitName} discount gives a lower total at this quantity. Shopify may choose that better discount.`;
        list.append(el('li','',instruction));
      });
      if(this.pendingOffers.length) list.append(el('li','','Some enabled offers are incomplete. Complete their settings before configuring discounts.'));
      container.append(list);
    }
    makeCard(tier,index) {
      const card = el('div','ub-card');
      card.dataset.offerSlot=String(tier.slot);
      const editorTemplate=[...this.querySelectorAll('[data-ub-editor-card]')].find(node=>node.dataset.ubEditorCard===tier.blockId);
      if(editorTemplate) [...editorTemplate.content.firstElementChild.attributes].forEach(attr=>card.setAttribute(attr.name,attr.value));
      const label = el('label','ub-label');
      const radio = el('input'); radio.type='radio'; radio.name=`ub-offer-${this.config.formId}`; radio.value=String(tier.quantity);
      const title = el('span','ub-title',this.offerTitle(tier.quantity));
      const saveBadge=el('span','ub-save-badge');
      const titleLine=el('span','ub-title-line'); titleLine.append(title,saveBadge);
      const saving=el('span','ub-saving');
      const customLabel=el('span','ub-custom-label');
      const subtitle=el('span','ub-subtitle');
      const description=el('span','ub-description'); description.append(titleLine,saving,subtitle,customLabel);
      const price = el('span','ub-price');
      label.append(radio,description,price);
      if(tier.badge) card.append(el('span','ub-ribbon',tier.badge));
      const details = el('div','ub-details'); details.hidden=true;
      const rows = el('div','ub-rows'); details.append(rows);
      let extra;
      if (tier.showQuantity) {
        const extraLabel=el('label','ub-extra',`Number of ${this.unitPlural}`);
        extra=el('input'); extra.type='number'; extra.min=String(tier.quantity); extra.max=String(tier.maxQuantity); extra.step='1'; extra.value=String(tier.quantity);
        extraLabel.append(extra); details.append(extraLabel);
        extra.addEventListener('input',()=> {
          const q=Number(extra.value);
          this.quantityValid=Number.isInteger(q) && q>=tier.quantity && q<=tier.maxQuantity;
          if (this.quantityValid) { this.quantity=q; this.renderRows(); this.updatePrices(); }
          this.updateState();
        });
      }
      radio.addEventListener('change',()=>this.selectTier(index));
      card.append(label,details); this.querySelector('.ub-offers').append(card);
      const result={tier,radio,price,details,rows,extra,saveBadge,saving,title,customLabel,subtitle};
      this.renderCardPrice(result,tier.quantity);
      return result;
    }
    makePendingCard(tier) {
      const card=el('div','ub-card');card.dataset.offerSlot=String(tier.slot);
      const editorTemplate=[...this.querySelectorAll('[data-ub-editor-card]')].find(node=>node.dataset.ubEditorCard===tier.blockId);
      if(editorTemplate) [...editorTemplate.content.firstElementChild.attributes].forEach(attr=>card.setAttribute(attr.name,attr.value));
      const summary=el('div','ub-label');
      const radio=el('input');radio.type='radio';radio.disabled=true;radio.setAttribute('aria-label',`Offer ${tier.slot} not configured`);
      summary.append(radio,el('span','ub-title',this.offerTitle(tier.quantity)),el('span','ub-price','Set up'));
      const message=el('p','ub-preview',tier.errors.join(' '));message.setAttribute('role','status');
      card.append(summary,message);this.querySelector('.ub-offers').append(card);
    }
    selectTier(index) {
      this.active=index; const card=this.cards[index];
      this.quantity=card.tier.quantity; this.quantityValid=true;
      if(card.tier.requireVariant) { this.selectedOptions=[]; this.selectedVariants=[]; }
      if (card.extra) card.extra.value=String(this.quantity);
      this.cards.forEach((c,i)=> { c.radio.checked=i===index; c.details.hidden=i!==index; c.rows.replaceChildren();
        this.renderCardPrice(c,c.tier.quantity);
      });
      this.renderRows(); this.updatePrices(); this.updateState();
    }
    renderRows() {
      const rows=this.cards[this.active].rows; rows.replaceChildren();
      for(let i=0;i<this.quantity;i++) {
        if (!this.selectedOptions[i]) {
          this.selectedOptions[i]=this.cards[this.active].tier.requireVariant ? this.config.options.map((_,j)=> {
            const values=[...new Set(this.variants.map(v=>v.options[j]))];return values.length===1 ? values[0] : null;
          }) : [...this.initial.options];
          this.resolveVariant(i);
        }
        if(!this.cards[this.active].tier.requireVariant && this.selectedOptions[i].some(v=>v===null)) {
          this.selectedOptions[i]=[...this.initial.options];this.resolveVariant(i);
        }
        const row=el('div','ub-pair'); row.append(el('span','ub-pair-number',`#${i+1}`));
        this.config.options.forEach((name,optionIndex)=> {
          const values=[...new Set(this.variants.map(v=>v.options[optionIndex]))];
          const isColour=/^(colou?r)$/i.test(name);
          const isVisual=isColour || /^variant$/i.test(name);
          if (isVisual) {
            const group=el('fieldset','ub-option ub-visual-options');
            const legend=el('legend','',`${name}: ${this.selectedOptions[i][optionIndex] || 'Choose'}`); group.append(legend);
            const choices=el('div','ub-swatches');
            values.forEach(value=> {
              const label=el('label','ub-swatch');
              const input=el('input'); input.type='radio'; input.name=`ub-option-${this.config.formId}-${i}-${optionIndex}`; input.value=value;
              input.checked=this.selectedOptions[i][optionIndex]===value;
              input.setAttribute('aria-label',`${this.unitName === 'item' ? 'Item' : 'Pair'} ${i+1}: ${name} ${value}`);
              const visual=this.config.optionVisuals?.[optionIndex]?.find(v=>v.value===value);
              const disc=el('span','ub-swatch-disc');
              // Use the actual variant photo for every visual option, including named colours.
              const image=visual?.image || visual?.swatchImage;
              if(image) { const img=el('img'); img.src=image; img.alt=''; img.width=52; img.height=52; disc.append(img); }
              else disc.append(el('span','',value.slice(0,2)));
              label.append(input,disc,el('span','ub-swatch-name',value)); choices.append(label);
              input.addEventListener('change',()=> {
                this.setOption(i,optionIndex,value);
                legend.textContent=`${name}: ${value}`;
              });
            });
            group.append(choices); row.append(group);
          } else {
            const label=el('label','ub-option',name); const select=el('select');
            select.setAttribute('aria-label',`${this.unitName === 'item' ? 'Item' : 'Pair'} ${i+1}: ${name}`);
            if(this.selectedOptions[i][optionIndex]===null) { const placeholder=el('option','',`Choose ${name.toLowerCase()}`);placeholder.value='';placeholder.disabled=true;placeholder.selected=true;select.append(placeholder); }
            values.forEach(value=> {
              const option=el('option','',value); option.value=value;
              option.selected=this.selectedOptions[i][optionIndex]===value; select.append(option);
            });
            select.addEventListener('change',()=>this.setOption(i,optionIndex,select.value));
            label.append(select); row.append(label);
          }
        }); rows.append(row);
      }
    }
    setOption(pair,index,value) {
      this.selectedOptions[pair][index]=value;
      this.resolveVariant(pair);
      this.updateState();
    }
    resolveVariant(pair) {
      const exact=this.variants.find(v=>v.options.every((v,j)=>v===this.selectedOptions[pair][j]));
      // Never silently substitute another size or colour.
      this.selectedVariants[pair]=exact?.id || null;
    }
    setupSizeChart() {
      const button=this.querySelector('.ub-size-chart-link');
      const dialog=this.querySelector('.ub-size-chart-dialog');
      const close=this.querySelector('.ub-chart-close');
      if(!button || !dialog || !close) return;
      button.addEventListener('click',()=>{dialog.showModal();close.focus();},{signal:this.controller.signal});
      close.addEventListener('click',()=>dialog.close(),{signal:this.controller.signal});
      dialog.addEventListener('click',event=>{if(event.target===dialog) {
        const rect=dialog.getBoundingClientRect();
        if(event.clientX<rect.left || event.clientX>rect.right || event.clientY<rect.top || event.clientY>rect.bottom) dialog.close();
      }},{signal:this.controller.signal});
      dialog.addEventListener('close',()=>button.focus(),{signal:this.controller.signal});
    }
    offerTitle(quantity) {
      const labels={
        1:`Buy 1 ${this.unitName}`,
        2:'Buy 1, get 1 FREE',
        3:'Ultimate 3-Pack',
        4:'Buy 2, get 2 FREE'
      };
      return labels[quantity] || `Buy ${quantity} ${this.unitPlural}`;
    }
    renderCardPrice(card,quantity) {
      const tier=card.tier;
      const total=tier.showQuantity ? tier.total/tier.quantity*quantity : tier.total;
      const original=Math.round(tier.original/tier.quantity*quantity);
      const hasOriginal=Number.isSafeInteger(original) && original>total;
      card.price.replaceChildren(el('span','ub-sale',this.money(total)));
      if(hasOriginal) {
        const strike=el('s','ub-original',this.money(original));
        strike.setAttribute('aria-label',`Original offer total ${strike.textContent}`);
        card.price.append(strike);
      }
      const perPair=Math.ceil(total/quantity/100);
      card.saveBadge.hidden=!Number.isFinite(perPair);
      card.saveBadge.textContent=Number.isFinite(perPair) ? String.fromCharCode(36)+perPair+` /${this.unitName}` : '';
      // Merchant-authored copy is plain text; Shopify reserves {{...}} for dynamic sources.
      const text=value=>String(value || '');
      card.title.textContent=this.offerTitle(quantity);
      card.saving.textContent=text(tier.savingsText);
      card.subtitle.textContent=text(tier.subtitle);card.subtitle.hidden=!card.subtitle.textContent;
      card.saving.hidden=!card.saving.textContent;
      card.customLabel.textContent=text(tier.label);card.customLabel.hidden=!card.customLabel.textContent;
    }
    updatePrices() {
      this.renderCardPrice(this.cards[this.active],this.quantity);
      this.renderTotalSavings();
    }
    selectionValid() {
      return this.quantityValid && this.selectedVariants.slice(0,this.quantity).length===this.quantity && this.selectedVariants.slice(0,this.quantity).every(id=>this.variants.some(v=>v.id===id && v.available));
    }
    canPurchase() { return this.ready && !this.preview && this.selectionValid(); }
    lockButtons(locked) {
      const buttons=[...this.form.querySelectorAll('[type="submit"]'),...document.querySelectorAll('button[type="submit"],input[type="submit"]')];
      [...new Set(buttons)].filter(button=>button.form===this.form).forEach(button=>button.disabled=locked);
    }
    updateState() {
      const valid=this.selectionValid(); this.lockButtons(!this.ready || this.preview || !valid);
      this.message.textContent=!this.quantityValid ? 'Enter a whole number within the quantity range.' : !valid ? `Choose an available variant and size for every ${this.unitName}.` : '';
      const first=this.selectedVariants[0]; if (first) this.form.querySelector('[name="id"]').value=String(first);
    }
    writeItems(data) {
      [...data.keys()].filter(key=>key.startsWith('ub-option-')).forEach(key=>data.delete(key));
      data.delete('id'); data.delete('quantity'); data.delete(`ub-offer-${this.config.formId}`);
      const groups=new Map(); this.selectedVariants.slice(0,this.quantity).forEach(id=>groups.set(id,(groups.get(id)||0)+1));
      let i=0; groups.forEach((quantity,id)=>{data.set(`items[${i}][id]`,String(id));data.set(`items[${i}][quantity]`,String(quantity));i++;});
    }
    setupAddon() {
      const container=this.querySelector('[data-ub-addon]');
      if(!container) return;
      const select=container.querySelector('[data-ub-addon-variant]');
      const price=container.querySelector('[data-ub-addon-price]');
      const compare=container.querySelector('[data-ub-addon-compare]');
      const saving=container.querySelector('[data-ub-addon-saving]');
      const image=container.querySelector('[data-ub-addon-image]');
      const checkbox=container.querySelector('[data-ub-addon-check]');
      const update=()=> {
        const option=select.selectedOptions?.[0] || select;
        const original=Number(option.dataset.price);
        const discount=Number(container.dataset.ubAddonDiscount || 50);
        const sale=Number.isFinite(original) ? Math.round(original*(100-discount)/100) : NaN;
        if(image) {
          image.src=option.dataset.image || image.dataset.fallback || image.src;
          const variantName=select.selectedOptions?.[0]?.textContent?.trim();
          image.alt=variantName && variantName!=='Default Title' ? `${container.dataset.productTitle || image.alt} — ${variantName}` : (container.dataset.productTitle || image.alt);
        }
        if(Number.isFinite(sale)) price.textContent=this.money(sale);
        if(Number.isFinite(original) && original>sale) {
          compare.textContent=this.money(original); compare.hidden=false;
          saving.textContent=`Save ${this.money(original-sale)}!`; saving.hidden=false;
        } else { compare.textContent=''; compare.hidden=true; saving.textContent=''; saving.hidden=true; }
        this.renderTotalSavings();
      };
      select.addEventListener('change',update);
      checkbox?.addEventListener('change',()=>this.renderTotalSavings());
      update();
    }
    renderTotalSavings() {
      const summary=this.totalSavingsSummary || this.querySelector('[data-ub-total-savings]');
      const value=summary?.querySelector('[data-ub-total-savings-value]');
      if(!summary || !value || !this.cards?.length || !this.tiers?.length) return;
      const tier=this.cards[this.active].tier;
      const offerTotal=tier.showQuantity ? tier.total/tier.quantity*this.quantity : tier.total;
      const originalTotal=Number.isSafeInteger(tier.original) ? Math.round(tier.original/tier.quantity*this.quantity) : offerTotal;
      const bundleSaving=Math.max(0,originalTotal-offerTotal);
      const addon=this.querySelector('[data-ub-addon]');
      const checkbox=addon?.querySelector('[data-ub-addon-check]');
      const selector=addon?.querySelector('[data-ub-addon-variant]');
      let addonSaving=0;
      let legacyAddonPrice=0;
      if(checkbox?.checked && selector?.value) {
        const option=selector.selectedOptions?.[0] || selector;
        const base=Number(option.dataset.price);
        const discount=Number(addon.dataset.ubAddonDiscount || 50);
        if(Number.isFinite(base)) {
          legacyAddonPrice=Math.round(base*(100-discount)/100);
          addonSaving=base-legacyAddonPrice;
        }
      }
      const ampAddon=window.UpliefAmpAddon?.getOfferPricing(this.form) || {priceCents:0,savingsCents:0};
      const totalSavings=bundleSaving+addonSaving+ampAddon.savingsCents;
      summary.hidden=totalSavings<=0;
      value.textContent=this.money(totalSavings);
    }
  });
})();
